/**
 * AppShell — responsive layout component for the SmartSafe Portal.
 *
 * Structure:
 * - Sidebar: Navigation component with collapse/expand for mobile
 * - Header: notification bell with badge, locale switcher, user avatar
 * - Content: renders children (route outlet)
 *
 * Mobile-first design starting at 360px viewport.
 * Requirements: 1.1, 1.4, 1.6, 33.1, 33.2
 */

import { NEW_PORTAL_ROLES, PORTAL_USER_ROLE_LABELS } from '@smartsafe/shared';
import { useState, useCallback, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useRouterState } from '@tanstack/react-router';

import { Navigation } from './Navigation';
import { AlertBadge } from './alert-badge';
import NotificationsSidesheet from './NotificationsSidesheet';
import { useAuthStore } from '../stores/auth-store';
import { signOut } from '../providers/AuthProvider';
import { useWsQueryUpdater } from '../hooks/useWsQueryUpdater';
import { ROUTE_CONFIG } from '../router/route-config';
import type { PortalUserRole } from '../types/portal-role';

/** CSS-in-JS styles for the AppShell — aligned to prototype (navy + gold) */
const styles = {
  shell: {
    display: 'flex',
    minHeight: '100vh',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    backgroundColor: 'var(--bg)',
  } as React.CSSProperties,
  sidebar: {
    width: '224px',
    backgroundColor: 'var(--surface)',
    color: 'var(--text-1)',
    display: 'flex',
    flexDirection: 'column',
    transition: 'transform 0.3s ease, width 0.3s ease',
    position: 'fixed',
    top: 0,
    left: 0,
    bottom: 0,
    zIndex: 1000,
    overflowY: 'auto',
    borderRight: '1px solid var(--border)',
  } as React.CSSProperties,
  sidebarCollapsed: {
    transform: 'translateX(-100%)',
  } as React.CSSProperties,
  sidebarHeader: {
    padding: '18px 16px 14px',
    borderBottom: '1px solid var(--border)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '10px',
  } as React.CSSProperties,
  logoContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  } as React.CSSProperties,
  logoText: {
    display: 'flex',
    flexDirection: 'column',
    lineHeight: 1.2,
  } as React.CSSProperties,
  logoTopLine: {
    display: 'flex',
    alignItems: 'baseline',
    gap: '0px',
  } as React.CSSProperties,
  logoSmart: {
    fontSize: '14px',
    fontWeight: 700,
    color: 'var(--blue-light)',
    letterSpacing: '0.5px',
  } as React.CSSProperties,
  logoSafe: {
    fontSize: '14px',
    fontWeight: 700,
    color: 'var(--text-1)',
    letterSpacing: '3px',
  } as React.CSSProperties,
  logoPlatform: {
    fontSize: '9px',
    fontWeight: 500,
    color: 'var(--gold)',
    letterSpacing: '2px',
    marginTop: '2px',
  } as React.CSSProperties,
  collapseBtn: {
    background: 'none',
    border: 'none',
    color: 'var(--text-2)',
    cursor: 'pointer',
    fontSize: '18px',
    padding: '4px 8px',
    borderRadius: 'var(--r-xs)',
  } as React.CSSProperties,
  roleWrap: {
    padding: '10px 12px',
    borderBottom: '1px solid var(--border)',
  } as React.CSSProperties,
  roleLabel: {
    fontSize: '10px',
    color: 'var(--text-3)',
    textTransform: 'uppercase',
    letterSpacing: '0.8px',
    marginBottom: '5px',
    fontWeight: 500,
  } as React.CSSProperties,
  roleSelect: {
    width: '100%',
    background: 'var(--card)',
    border: '1px solid var(--border-2)',
    borderRadius: 'var(--r-sm)',
    color: 'var(--text-1)',
    padding: '7px 10px',
    fontSize: '12px',
    fontWeight: 500,
    outline: 'none',
    cursor: 'pointer',
    paddingRight: '28px',
    WebkitAppearance: 'none',
    appearance: 'none',
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%237A9AB8' d='M2 4l4 4 4-4'/%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 10px center',
  } as React.CSSProperties,
  sidebarBottom: {
    borderTop: '1px solid var(--border)',
    padding: '10px 12px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginTop: 'auto',
  } as React.CSSProperties,
  sidebarUserAvatar: {
    width: '30px',
    height: '30px',
    borderRadius: '50%',
    backgroundColor: 'var(--gold-dim)',
    border: '1px solid var(--gold)',
    color: 'var(--gold)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '11px',
    fontWeight: 700,
    flexShrink: 0,
  } as React.CSSProperties,
  sidebarUserInfo: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minWidth: 0,
  } as React.CSSProperties,
  sidebarUserName: {
    fontSize: '12px',
    fontWeight: 600,
    color: 'var(--text-1)',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  } as React.CSSProperties,
  sidebarRoleBadge: {
    fontSize: '9px',
    fontWeight: 600,
    color: 'var(--gold)',
    backgroundColor: 'var(--gold-dim)',
    padding: '2px 6px',
    borderRadius: '3px',
    marginTop: '3px',
    width: 'fit-content',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  } as React.CSSProperties,
  sidebarActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    flexShrink: 0,
  } as React.CSSProperties,
  sidebarActionBtn: {
    background: 'none',
    border: 'none',
    color: 'var(--text-2)',
    cursor: 'pointer',
    fontSize: '16px',
    padding: '4px 6px',
    borderRadius: 'var(--r-xs)',
    transition: 'color 0.12s',
  } as React.CSSProperties,
  mainArea: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    marginLeft: '224px',
    transition: 'margin-left 0.3s ease',
  } as React.CSSProperties,
  mainAreaCollapsed: {
    marginLeft: 0,
  } as React.CSSProperties,
  header: {
    height: '56px',
    backgroundColor: 'var(--surface)',
    borderBottom: '1px solid var(--border)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 24px',
    position: 'sticky',
    top: 0,
    zIndex: 900,
  } as React.CSSProperties,
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  } as React.CSSProperties,
  headerTitle: {
    fontSize: '16px',
    fontWeight: 700,
    color: 'var(--text-1)',
  } as React.CSSProperties,
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  } as React.CSSProperties,
  menuBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '24px',
    padding: '4px 8px',
    color: 'var(--text-2)',
    display: 'none',
  } as React.CSSProperties,
  menuBtnVisible: {
    display: 'block',
  } as React.CSSProperties,
  notificationBtn: {
    position: 'relative',
    background: 'transparent',
    border: '1px solid var(--border)',
    cursor: 'pointer',
    fontSize: '16px',
    width: '32px',
    height: '32px',
    borderRadius: 'var(--r-xs)',
    color: 'var(--text-2)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  } as React.CSSProperties,
  badge: {
    position: 'absolute',
    top: '-4px',
    right: '-4px',
    backgroundColor: 'var(--offline)',
    color: '#fff',
    fontSize: '10px',
    fontWeight: 700,
    borderRadius: '10px',
    minWidth: '18px',
    height: '18px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 4px',
  } as React.CSSProperties,
  localeSwitcher: {
    padding: '6px 10px',
    border: '1px solid var(--border-2)',
    borderRadius: 'var(--r-sm)',
    backgroundColor: 'var(--card)',
    color: 'var(--text-1)',
    fontSize: '12px',
    cursor: 'pointer',
  } as React.CSSProperties,
  scopeSelector: {
    padding: '6px 14px',
    border: '1px solid var(--border-2)',
    borderRadius: '16px',
    backgroundColor: 'transparent',
    color: 'var(--text-1)',
    fontSize: '12px',
    cursor: 'pointer',
  } as React.CSSProperties,
  bellDot: {
    position: 'absolute',
    top: '2px',
    right: '2px',
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: 'var(--offline, #F04040)',
  } as React.CSSProperties,
  userAvatar: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    backgroundColor: 'var(--gold-dim)',
    border: '1px solid var(--gold)',
    color: 'var(--gold)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '13px',
    fontWeight: 700,
  } as React.CSSProperties,
  content: {
    flex: 1,
    padding: '24px',
    backgroundColor: 'var(--bg)',
    color: 'var(--text-1)',
  } as React.CSSProperties,
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    zIndex: 999,
  } as React.CSSProperties,
} as const;

/**
 * Role options shown in the "Perfil de Acesso" dropdown (Platform Admin only):
 * lets the admin preview the portal as another role, including the new ones.
 */
const ROLE_OPTIONS: { value: PortalUserRole; label: string }[] = (
  ['platform_admin', 'operator', 'client_admin', 'client_viewer', 'developer', ...NEW_PORTAL_ROLES] as PortalUserRole[]
).map((value) => ({ value, label: PORTAL_USER_ROLE_LABELS[value] }));

interface AppShellProps {
  children: ReactNode;
}

/**
 * Main application shell with responsive sidebar, header, and content area.
 */
export function AppShell({ children }: AppShellProps) {
  const { t } = useTranslation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [darkMode, setDarkMode] = useState(true);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    return window.matchMedia('(max-width: 767px)').matches;
  });
  const displayName = useAuthStore((s) => s.displayName);
  const role = useAuthStore((s) => s.role);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const switchRole = useAuthStore((s) => s.switchRole);

  const wsUrl = (import.meta.env.VITE_WS_URL as string | undefined) ?? '';
  useWsQueryUpdater({ url: wsUrl, enabled: Boolean(wsUrl) && isAuthenticated });

  // Get current pathname from TanStack Router for page title
  const currentPath = useRouterState({
    select: (state) => state.location.pathname,
  });

  // Resolve page title from route config
  const currentRoute = ROUTE_CONFIG.find((r) => {
    if (r.path === '/') return currentPath === '/';
    return currentPath.startsWith(r.path) && !r.path.includes(':');
  });
  const pageTitle = currentRoute?.label ?? 'SmartSafe Next';

  // Handle responsive behavior
  if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
    const mql = window.matchMedia('(max-width: 767px)');
    if (mql.matches !== isMobile) {
      setIsMobile(mql.matches);
      if (mql.matches) {
        setSidebarOpen(false);
      }
    }
  }

  const toggleSidebar = useCallback(() => {
    setSidebarOpen((prev) => !prev);
  }, []);

  const closeSidebar = useCallback(() => {
    setSidebarOpen(false);
  }, []);

  const handleThemeToggle = useCallback(() => {
    setDarkMode((prev) => {
      const next = !prev;
      if (typeof document !== 'undefined') {
        document.documentElement.setAttribute('data-theme', next ? 'dark' : 'light');
      }
      return next;
    });
  }, []);

  const handleLogout = useCallback(() => {
    void signOut();
  }, []);

  const handleRoleSwitch = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      switchRole(e.target.value as PortalUserRole);
    },
    [switchRole],
  );

  const userName = displayName ?? 'Platform Admin';
  const userInitials = (() => {
    if (!displayName) return 'A';
    const parts = displayName.trim().split(/\s+/).filter(Boolean);
    const first = parts[0] ?? '';
    if (parts.length >= 2) {
      const last = parts[parts.length - 1] ?? '';
      return ((first[0] ?? '') + (last[0] ?? '')).toUpperCase() || 'A';
    }
    return (first[0] ?? '').toUpperCase() || 'A';
  })();
  const roleDisplay = role ? PORTAL_USER_ROLE_LABELS[role] : 'Admin';

  const sidebarStyle = {
    ...styles.sidebar,
    ...(isMobile && !sidebarOpen ? styles.sidebarCollapsed : {}),
    ...(isMobile ? {} : { position: 'fixed' as const }),
  };

  const mainStyle = {
    ...styles.mainArea,
    ...(isMobile || !sidebarOpen ? styles.mainAreaCollapsed : {}),
  };

  return (
    <div style={styles.shell} data-testid="app-shell">
      {/* Mobile overlay */}
      {isMobile && sidebarOpen && (
        <div
          style={styles.overlay}
          onClick={closeSidebar}
          aria-hidden="true"
          data-testid="sidebar-overlay"
        />
      )}

      {/* Sidebar */}
      <aside style={sidebarStyle} aria-label="Menu lateral" data-testid="sidebar">
        <div style={styles.sidebarHeader}>
          <div style={styles.logoContainer}>
            {/* Logo SVG — lock with wifi waves */}
            <svg viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg" width="34" height="34" aria-hidden="true">
              <rect width="36" height="36" rx="8" fill="#0D1C2E" />
              <rect x="10" y="18" width="16" height="12" rx="3" fill="#B8965A" />
              <path d="M13 18v-4a5 5 0 0110 0v4" stroke="#B8965A" strokeWidth="2.2" strokeLinecap="round" fill="none" />
              <circle cx="18" cy="24" r="2" fill="#0D1C2E" />
              <path d="M14 11.5a6 6 0 018 0" stroke="#D4AE70" strokeWidth="1.5" strokeLinecap="round" fill="none" opacity="0.6" />
              <path d="M11.5 9a9.5 9.5 0 0113 0" stroke="#D4AE70" strokeWidth="1.2" strokeLinecap="round" fill="none" opacity="0.3" />
            </svg>
            <div style={styles.logoText}>
              <div style={styles.logoTopLine}>
                <span style={styles.logoSmart}>SMART</span>
                <span style={styles.logoSafe}>SAFE</span>
              </div>
              <span style={styles.logoPlatform}>NEXT PLATFORM</span>
            </div>
          </div>
          <button
            style={styles.collapseBtn}
            onClick={toggleSidebar}
            aria-label={sidebarOpen ? t('sidebar.collapse') : t('sidebar.expand')}
            data-testid="sidebar-toggle"
          >
            {sidebarOpen ? '✕' : '☰'}
          </button>
        </div>

        {/* Role switcher — Perfil de Acesso (visível apenas para Platform Admin) */}
        {role === 'platform_admin' && (
          <div style={styles.roleWrap} data-testid="role-switcher">
            <div style={styles.roleLabel}>Perfil de Acesso</div>
            <select
              style={styles.roleSelect}
              value={role}
              onChange={handleRoleSwitch}
              aria-label="Selecionar perfil de acesso"
              data-testid="role-select"
            >
              {ROLE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        )}

        <Navigation />

        {/* Sidebar bottom — user info */}
        <div style={styles.sidebarBottom} data-testid="sidebar-user">
          <div style={styles.sidebarUserAvatar} aria-hidden="true">
            {userInitials}
          </div>
          <div style={styles.sidebarUserInfo}>
            <span style={styles.sidebarUserName}>{userName}</span>
            <span style={styles.sidebarRoleBadge}>{roleDisplay}</span>
          </div>
          <div style={styles.sidebarActions}>
            <button
              style={styles.sidebarActionBtn}
              onClick={handleThemeToggle}
              aria-label={darkMode ? 'Ativar tema claro' : 'Ativar tema escuro'}
              data-testid="theme-toggle"
            >
              {darkMode ? '🌙' : '☀️'}
            </button>
            <button
              style={styles.sidebarActionBtn}
              onClick={handleLogout}
              aria-label="Sair"
              data-testid="logout-btn"
            >
              ⏻
            </button>
          </div>
        </div>
      </aside>

      {/* Main area */}
      <div style={mainStyle}>
        {/* Header */}
        <header style={styles.header} role="banner" data-testid="header">
          <div style={styles.headerLeft}>
            <button
              style={{
                ...styles.menuBtn,
                ...(isMobile || !sidebarOpen ? styles.menuBtnVisible : {}),
              }}
              onClick={toggleSidebar}
              aria-label={t('sidebar.expand')}
              data-testid="mobile-menu-btn"
            >
              ☰
            </button>
            <span style={styles.headerTitle}>{pageTitle}</span>
          </div>

          <div style={styles.headerRight}>
            {/* Scope selector — aligned to prototype */}
            <select
              style={styles.scopeSelector}
              aria-label="Filtrar escopo"
              data-testid="scope-selector"
              defaultValue=""
            >
              <option value="">Toda a Plataforma</option>
            </select>

            {/* Notification bell with real M12 alert count */}
            <button
              style={styles.notificationBtn}
              aria-label={t('header.notifications')}
              data-testid="notification-bell"
              onClick={() => { setNotificationsOpen(true); }}
            >
              🔔
              <AlertBadge />
            </button>
          </div>
        </header>

        {/* Content area */}
        <main style={styles.content} role="main" data-testid="content-area">
          {children}
        </main>

        {/* Notifications Sidesheet */}
        <NotificationsSidesheet
          open={notificationsOpen}
          onClose={() => { setNotificationsOpen(false); }}
        />
      </div>
    </div>
  );
}
